import {useSWRPaginationTemplate, useSWRTemplate} from 'hooks/dataHooksTemplate';
import {api} from './common';
import {processCurrencyWithdrawalSupportMethod} from 'data/currencyWithdrawalSupportMethod';
import {processRegulationRegion} from 'data/regulationRegion';
import {processPlatformAccount} from 'data/platformAccount';
import {cleanObjectNilValue} from 'utils/clean';
import {flatten, get, keys} from 'lodash';

export const useWithdrawalSupportMethod = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['settings/withdrawal-support-method'],
        request: async ([_]) => {
            const {result} = await api().get([
                'setting',
                'withdrawal-support-method',
            ]);
            return result.map(processCurrencyWithdrawalSupportMethod);
        },
        defaultValue: [],
    });
    return swr;
};

export const useRegulationRegion = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['settings/regulationRegion'],
        request: async ([_]) => {
            const {result} = await api().get(['setting', 'regulation']);
            return result.map(processRegulationRegion);
        },
        defaultValue: [],
    });
    return swr;
};
export const usePlatformAccounts = (options = {}) => {
    const { currency } = options
    const swr = useSWRTemplate({
        key: ['settings/platformAccount', currency],
        request: async ([_, _currency]) => {
            const {result} = await api().get(['platform', 'bank'], cleanObjectNilValue({ currency: _currency }));
            return result.map(processPlatformAccount);
        },
        defaultValue: [],
    });
    return swr;
};

export const updatePlatformAccount_ = async (id, data) => {
    const {result} = await api().put(['platform', 'bank', id], data);
    return result;
}

export const useServiceFeeSetting = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['settings/serviceFeeSetting'],
        request: async ([_]) => {
            const {result} = await api().get(['setting', 'fee-setting']);
            return result.map((item) => ({
                ...item,
                value:
                    item.code === 'DEFAULT_SERVICE_FEE'
                        ? JSON.parse(item.value)
                        : item.value,
            }));
        },
        defaultValue: [],
    });
    return swr;
};

export const useAccountGroupSettings = (options = {}) => {
    const {type} = options;
    const swr = useSWRTemplate({
        key: type ? ['settings/accountGroupSettings', type] : null,
        request: async ([_, _type]) => {
            const {result} = await api().get(
                ['setting', 'group-setting'],
                cleanObjectNilValue({
                    type: _type,
                }),
            );
            const res = flatten(
                JSON.parse(get(result, 'value') || '[]').map((item) => {
                    return keys(item).map((key) => ({
                        currency: key,
                        ...item[key],
                    }));
                }),
            );
            return res;
        },
        defaultValue: [],
    });
    return swr;
};

export const updateAccountGroupSetting_ = async ({currency, type, data}) => {
    const {result} = await api().put(
        ['setting', 'group-setting', currency],
        cleanObjectNilValue(data),
        {type},
    );
    return result;
};
export const createAccountGroupSetting_ = async ({type, data}) => {
    const {result} = await api().post(
        ['setting', 'group-setting'],
        cleanObjectNilValue(data),
        {type},
    );
    return result;
};

export const createRestrictedRegion_ = async ({country}) => {
    const {result} = await api().put(['setting', 'restricted-region', country], { isRestricted: true, disabledRegister: true });
    return result;
};

export const setRegionRiskyLevel_ = async ({country, riskLevel}) => {
    const {result} = await api().put(
        ['setting', 'restricted-region', country],
        {
            riskRegion: riskLevel,
        },
    );
    return result;
};

export const removeRestrictedRegion_ = async ({country}) => {
    const {result} = await api().put(['setting', 'restricted-region', country], { isRestricted: false, disabledRegister: false });
    return result;
};

export const updateRegulationRegion_ = async (
    id,
    {
        name,
        isDefaultGlobal,
        isRestricted,
        region,
        leverage,
        ibVisibleLevel,
        ibVisibleDetailLevel,
        ibPercent,
        termsAndCondition,
        desc,
    },
) => {
    const {result} = await api().put(
        ['setting', 'regulation', id],
        cleanObjectNilValue({
            name,
            isDefaultGlobal,
            isRestricted,
            region,
            leverage,
            ibVisibleLevel,
            ibVisibleDetailLevel,
            ibPercent,
            termsAndCondition,
            desc,
        }),
    );
    return result;
};

export const createRegulationRegion_ = async ({
    name,
    isDefaultGlobal,
    isRestricted,
    region,
    leverage,
    ibVisibleLevel,
    ibVisibleDetailLevel,
    ibPercent,
    termsAndCondition,
    desc,
}) => {
    const {result} = await api().post(
        ['setting', 'regulation'],
        cleanObjectNilValue({
            name,
            isDefaultGlobal,
            isRestricted,
            region,
            leverage,
            ibVisibleLevel,
            ibVisibleDetailLevel,
            ibPercent,
            termsAndCondition,
            desc,
        }),
    );
    return result;
};

export const delRegulationRegion_ = async (id) => {
    const {result} = await api().del(['setting', 'regulation', id]);
    return result;
};


export const updateWithdrawalCurrency_ = async (
    id,
    {currency, routeType, paymentType, feeModel, feeValue, requireRule},
) => {
    const {result} = await api().put(
        ['setting', 'withdrawal-support-method', id],
        cleanObjectNilValue({
            type: 'withdrawal',
            currency,
            routeType,
            paymentType,
            feeModel,
            feeValue,
            requireRule,
        }),
    );
    return result;
};
export const createWithdrawalCurrency_ = async ({
    currency,
    routeType,
    paymentType,
    feeModel,
    feeValue,
    requireRule,
}) => {
    const {result} = await api().post(
        ['setting', 'withdrawal-support-method'],
        cleanObjectNilValue({
            type: 'withdrawal',
            currency,
            routeType,
            paymentType,
            feeModel,
            feeValue,
            requireRule,
        }),
    );
    return result;
};
export const delWithdrawalCurrency_ = async (id) => {
    const {result} = await api().del([
        'setting',
        'withdrawal-support-method',
        id,
    ]);
    return result;
};


export const useDataRollOver = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['settings/data-roll-over'],
        request: async ([_]) => {
            const {result} = await api().get(['rollup', 'status']);
            return result.map(item=>{
                return {
                    ...item,
                }
            });
        },
        defaultValue: [],
    });
    return {
        ...swr,
        updateRollOver: async (source, params) => {
            const {result} = await api().put(['rollup', 'status', source], cleanObjectNilValue(params));
            return result;
        }
    };
};

export const useProductRestriction = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['product-restriction'],
        request: async ([_]) => {
            const {result} = await api().get(['setting', 'product-restriction']);
            return get(result, 'data', []);
        },
        defaultValue: [],
    });
    return swr;
};

export const addProductRestriction_ = async ({productCode, country}) => {
    const {result} = await api().post(['setting', 'product-restriction'], {
        productCode,
        country,
    });
    return result;
};

export const deleteProductRestrictionById_ = async (id) => {
    const {result} = await api().del(['setting', 'product-restriction', id]);
    return result;
};

export const checkUserProductAccess_ = async ({userId, productCode}) => {
    const {result} = await api().get(['product-restriction', 'check'], {
        userId,
        productCode,
    });
    return result;
};


export const useRestrictionEmailDomain = (options = {}) => {
    const { domain } = options;
    const swr = useSWRPaginationTemplate({
        key: ['restriction-email-domain', domain],
        request: async ([_], _pageIndex, _pageSize) => {
            const query = {
                pageIndex: _pageIndex - 1,
                pageSize: _pageSize,
                domain
            }
            const {result} = await api().get(['setting', 'verify-email-setting'], cleanObjectNilValue(query));
            return {result}
        },
        defaultValue: {items: [], totalItems: 0},
    });
    return swr;
};

export const addRestrictionEmailDomain_ = async ({domain}) => {
    const {result} = await api().post(['setting', 'verify-email-setting'], { domain });
    return result;
};

export const deleteRestrictionEmailDomain_ = async (id) => {
    const {result} = await api().del(['setting', 'verify-email-setting', id]);
    return result;
};